import Constants from './Constants';
import DateUtils from './DateUtils';
export class CommonFileUtils {
    // US Letter size in points
    static PAGE_WIDTH = 612;
    static PAGE_HEIGHT = 792;
    static PAGE_HEADER_HEIGHT = 70;
    static TITLE_SIZE = 18;
    static TITLE_Y = this.PAGE_HEADER_HEIGHT + 30;
    static BODY_Y = this.TITLE_Y + this.TITLE_SIZE + 20;
    static FOOTER_Y = this.PAGE_HEIGHT - 26;
    /** Rule drawn above the footer text. */
    static FOOTER_RULE_Y = this.FOOTER_Y - 30;
    /** Lowest baseline page content may use before it collides with the footer. */
    static CONTENT_MAX_Y = this.FOOTER_RULE_Y - 8;
    /** Landscape canvas size for blank scrapbook pages. */
    static BLANK_PAGE_WIDTH = 800;
    static BLANK_PAGE_HEIGHT = 600;
    static MARGIN = 24;
    static FONT = 'Arial, Helvetica, sans-serif';
    static SMALL_FONT_SIZE = 9;
    static SMALL_LINE_HEIGHT = 12;
    static BODY_SIZE = 11;
    static LINE_HEIGHT = 16;
    static LABEL_WIDTH = 120;
    static SECTION_GAP = 14;
    /** Padding inside a `CoverField.border` box, and extra gap above it. */
    static BORDER_PAD = 8;
    static nextBlankPageId(existingPages = []) {
        let max = 0;
        for (const page of existingPages) {
            const n = parseInt(page._id, 10);
            if (Number.isFinite(n) && String(n) === page._id) {
                max = Math.max(max, n);
            }
        }
        return String(max + 1);
    }
    static createBlankFilePage(fileId, existingPages = []) {
        return {
            _id: this.nextBlankPageId(existingPages),
            file_id: fileId,
            thumb_width: this.BLANK_PAGE_WIDTH,
            thumb_height: this.BLANK_PAGE_HEIGHT,
        };
    }
    static sortPagesByIdAsc(pages) {
        return [...pages].sort((a, b) => {
            const an = parseInt(a._id, 10);
            const bn = parseInt(b._id, 10);
            const aNumeric = Number.isFinite(an) && String(an) === a._id;
            const bNumeric = Number.isFinite(bn) && String(bn) === b._id;
            if (aNumeric && bNumeric) {
                return an - bn;
            }
            return a._id.localeCompare(b._id);
        });
    }
    /**
     * Avg glyph width as a fraction of fontSize for Arial/Helvetica body text.
     * Used by cover-page field wrapping (matches RFI cover layout).
     */
    static WRAP_CHAR_WIDTH_RATIO = 0.46;
    static findExistingFile(files, name, fileSize) {
        return files.find((f) => f.name === name && f.num_bytes === fileSize);
    }
    static getLogoDimensions(logo, maxWidth, maxHeight) {
        if (!logo?.data || !logo.width || !logo.height)
            return { width: 0, height: 0 };
        const ratio = Math.min(maxWidth / logo.width, maxHeight / logo.height);
        return { width: logo.width * ratio, height: logo.height * ratio };
    }
    static renderLogo(logo, x, y, maxWidth, maxHeight) {
        if (!logo?.data)
            return '';
        const { width, height } = this.getLogoDimensions(logo, maxWidth, maxHeight);
        if (!width || !height)
            return '';
        if (logo.mimeType === 'image/svg+xml') {
            return this.renderInlineSvgLogo(logo.data, x, y, width, height, logo.width, logo.height);
        }
        const dataUri = this.getRasterLogoDataUri(logo);
        // Nested svg carries xmlns:xlink so xlink:href binds when this markup is
        // injected via HTML innerHTML (bare href on <image> is ignored in that path).
        return `<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" x="${x}" y="${y}" width="${width}" height="${height}">
  <image href="${dataUri}" xlink:href="${dataUri}" width="${width}" height="${height}" preserveAspectRatio="xMidYMid meet"/>
</svg>`;
    }
    static getRasterLogoDataUri(logo) {
        if (logo.data.startsWith('data:'))
            return logo.data;
        if (logo.mimeType)
            return `data:${logo.mimeType};base64,${logo.data}`;
        return this.getImageDataUri(logo.data);
    }
    static getImageDataUri(base64) {
        if (base64.startsWith('data:'))
            return base64;
        if (base64.startsWith('/9j/'))
            return `data:image/jpeg;base64,${base64}`;
        if (base64.startsWith('iVBOR'))
            return `data:image/png;base64,${base64}`;
        if (base64.startsWith('R0lGOD'))
            return `data:image/gif;base64,${base64}`;
        if (base64.startsWith('UklGR'))
            return `data:image/webp;base64,${base64}`;
        if (base64.startsWith('PD94bWwg') || base64.startsWith('PHN2Zy'))
            return `data:image/svg+xml;base64,${base64}`;
        return `data:image/jpeg;base64,${base64}`;
    }
    /** Alias for {@link getImageDataUri}. */
    static getThumbnailDataUri(thumb) {
        return this.getImageDataUri(thumb);
    }
    static renderHeader(company, project) {
        const logo = this.renderLogo(company?.logo, this.MARGIN, this.MARGIN, 150, 50);
        const companyTextX = this.MARGIN + 170;
        const companyLines = [
            company?.name || '',
            ...(company?.address ? this.getAddressLines(company.address, company.city, company.state_code, company.zip) : []),
            company?.phone ? `P: ${company.phone}` : '',
        ]
            .filter(Boolean)
            .map((line, index) => `<tspan x="${companyTextX}" dy="${index === 0 ? 0 : this.SMALL_LINE_HEIGHT}">${this.escapeXml(line)}</tspan>`)
            .join('');
        const projectLines = project
            ? [
                project.name,
                ...this.getAddressLines(project.address, project.city, project.state_code, project.zip),
                project.job_number ? `Job Number: ${project.job_number}` : '',
            ].filter(Boolean)
            : [];
        const projectText = projectLines
            .map((line, index) => `<tspan x="${this.PAGE_WIDTH - this.MARGIN}" dy="${index === 0 ? 0 : this.SMALL_LINE_HEIGHT}">${this.escapeXml(line)}</tspan>`)
            .join('');
        return `${logo}
  <text x="${companyTextX}" y="${this.MARGIN}" font-family="${this.FONT}" font-size="${this.SMALL_FONT_SIZE}" fill="#000000">${companyLines}</text>
  <text x="${this.PAGE_WIDTH - this.MARGIN}" y="${this.MARGIN}" text-anchor="end" font-family="${this.FONT}" font-size="${this.SMALL_FONT_SIZE}" fill="#000000">${projectText}</text>
  <line x1="${this.MARGIN}" y1="${this.PAGE_HEADER_HEIGHT}" x2="${this.PAGE_WIDTH - this.MARGIN}" y2="${this.PAGE_HEADER_HEIGHT}" stroke="#cccccc" stroke-width="1"/>`;
    }
    static renderTitle(text) {
        return `<text x="${this.MARGIN}" y="${this.PAGE_HEADER_HEIGHT + 30}" font-family="${this.FONT}" font-size="${this.TITLE_SIZE}" fill="#000000">${text}</text>`;
    }
    static renderFooter(company, pageNumber, totalPages) {
        const footerY = this.FOOTER_Y;
        const printedOn = this.getPrintedOnLabel();
        return `<line x1="${this.MARGIN}" y1="${this.FOOTER_RULE_Y}" x2="${this.PAGE_WIDTH - this.MARGIN}" y2="${this.FOOTER_RULE_Y}" stroke="#e0e0e0" stroke-width="1"/>
  <text x="${this.MARGIN}" y="${footerY}" font-family="${this.FONT}" font-size="11" fill="#222222">${this.escapeXml(company?.name || '')}</text>
  <text x="${this.PAGE_WIDTH / 2}" y="${footerY}" text-anchor="middle" font-family="${this.FONT}" font-size="11" fill="#222222">Page ${pageNumber} of ${totalPages}</text>
  <text x="${this.PAGE_WIDTH - this.MARGIN}" y="${footerY}" text-anchor="end" font-family="${this.FONT}" font-size="11" fill="#222222">${this.escapeXml(printedOn)}</text>`;
    }
    static renderFieldColumn(fields, x, startY, width, options = {}) {
        const labelWidth = options.labelWidth ?? this.LABEL_WIDTH;
        const multilineGap = options.multilineGap ?? this.SECTION_GAP;
        let y = startY;
        const parts = [];
        for (const field of fields) {
            if (field.multiline) {
                const block = this.renderMultilineField(field.label, field.value, x, y, width, { border: field.border });
                parts.push(block.svg);
                y = block.endY + multilineGap;
            }
            else {
                parts.push(this.renderFieldRow(field.label, field.value, x, y, width, labelWidth));
                y += this.LINE_HEIGHT;
            }
        }
        return { svg: parts.join('\n  '), endY: y };
    }
    static renderFieldRow(label, value, x, y, width, labelWidth = this.LABEL_WIDTH) {
        const lines = this.wrapText(value, width - labelWidth, this.BODY_SIZE);
        const firstValue = lines[0] ?? '';
        const continuation = lines
            .slice(1)
            .map((line, index) => `<tspan x="${x + labelWidth}" dy="${index === 0 ? this.LINE_HEIGHT : this.LINE_HEIGHT}">${this.escapeXml(line)}</tspan>`)
            .join('');
        return `<text x="${x}" y="${y}" font-family="${this.FONT}" font-size="${this.BODY_SIZE}" fill="#000000">
    <tspan font-weight="bold">${this.escapeXml(label)}</tspan>
    <tspan dx="4" font-weight="normal">${this.escapeXml(firstValue)}</tspan>${continuation}
  </text>`;
    }
    /**
     * Label on its own line; value wraps to full column width.
     * Blank paragraphs use NBSP so SVG tspans still paint and honor dy.
     */
    static renderMultilineField(label, value, x, y, width, options = {}) {
        const pad = this.BORDER_PAD;
        // Keep the box below the previous field: extra gap, then the rect starts at boxTop.
        const boxTop = options.border ? y + pad : y;
        const contentY = options.border ? boxTop + pad + this.BODY_SIZE : y;
        const labelSvg = `<text x="${x}" y="${contentY}" font-family="${this.FONT}" font-size="${this.BODY_SIZE}" fill="#000000">
    <tspan font-weight="bold">${this.escapeXml(label)}</tspan>
  </text>`;
        const lines = this.wrapText(value, width, this.BODY_SIZE);
        const valueEndY = lines.length === 0 ? contentY : contentY + this.LINE_HEIGHT * lines.length;
        const valueSvg = lines.length === 0
            ? ''
            : (() => {
                const valueY = contentY + this.LINE_HEIGHT;
                const tspans = lines
                    .map((line, index) => {
                    const content = line.length === 0 ? '\u00A0' : this.escapeXml(line);
                    return index === 0
                        ? `<tspan>${content}</tspan>`
                        : `<tspan x="${x}" dy="${this.LINE_HEIGHT}">${content}</tspan>`;
                })
                    .join('');
                return `<text x="${x}" y="${valueY}" font-family="${this.FONT}" font-size="${this.BODY_SIZE}" fill="#000000">${tspans}</text>`;
            })();
        const borderSvg = options.border
            ? `<rect x="${x - pad}" y="${boxTop}" width="${width + pad * 2}" height="${valueEndY - boxTop + pad}" fill="none" stroke="${options.border}" stroke-width="1.5" rx="2"/>`
            : '';
        return {
            svg: [borderSvg, labelSvg, valueSvg].filter(Boolean).join('\n  '),
            endY: valueEndY + (options.border ? pad : 0),
        };
    }
    /**
     * Circular avatar: person photo when present, otherwise initials.
     * Appends any clipPath defs into `defsOut`.
     */
    static renderPersonAvatar(person, x, centerY, size, clipId, defsOut) {
        const radius = size / 2;
        const cx = x + radius;
        const cy = centerY;
        const fill = person?.color || '#6b7280';
        if (person?.image?.data && person.image.mimeType) {
            defsOut.push(`<clipPath id="${clipId}"><circle cx="${cx}" cy="${cy}" r="${radius}"/></clipPath>`);
            const dataUri = person.image.data.startsWith('data:')
                ? person.image.data
                : `data:${person.image.mimeType};base64,${person.image.data}`;
            return `<circle cx="${cx}" cy="${cy}" r="${radius}" fill="#e5e7eb"/>
  <image href="${dataUri}" x="${x}" y="${cy - radius}" width="${size}" height="${size}" preserveAspectRatio="xMidYMid slice" clip-path="url(#${clipId})"/>`;
        }
        const initials = this.getPersonInitials(person);
        const initialsSize = Math.round(size * 0.4);
        return `<circle cx="${cx}" cy="${cy}" r="${radius}" fill="${this.escapeXml(fill)}"/>
  <text x="${cx}" y="${cy}" text-anchor="middle" dominant-baseline="middle" font-family="${this.FONT}" font-size="${initialsSize}" font-weight="bold" fill="#ffffff">${this.escapeXml(initials)}</text>`;
    }
    /**
     * Full-width top-of-page document badge for exports.
     * Matches cover header logo size/position (MARGIN, 200×50); height hugs
     * logo/text with a divider just underneath.
     * Lines (right of logo):
     *   [avatar] {name}
     *            {person} uploaded on {date} ({N} pages)
     */
    static generateFileBadge(file, options = {}) {
        const title = (options.title ?? file.name ?? 'Untitled').trim() || 'Untitled';
        const pageCount = options.pageCount ?? file.num_pages ?? 0;
        const pageLabel = pageCount === 1 ? '1 page' : `${pageCount || '—'} pages`;
        const uploader = (options.uploaderName || '').trim() ||
            (options.person ? `${options.person.first_name || ''} ${options.person.last_name || ''}`.trim() : '') ||
            'Unknown';
        const dateLabel = file.created_date ? DateUtils.formatIso(file.created_date) : 'Unknown date';
        const logo = options.logo ?? Constants.DEFAULT_COMPANY.logo;
        const line1 = `${title}`;
        const line2 = `${uploader} uploaded on ${dateLabel} (${pageLabel})`;
        const width = options.width ?? this.PAGE_WIDTH;
        const logoMaxWidth = 200;
        const logoMaxHeight = 50;
        const { height: logoHeight } = this.getLogoDimensions(logo, logoMaxWidth, logoMaxHeight);
        const avatarSize = 32;
        const avatarGap = 8;
        const avatarX = this.MARGIN + 220;
        const textX = avatarX + avatarSize + avatarGap;
        const textColor = '#333333';
        const subtextColor = '#555555';
        const y1 = this.MARGIN + this.BODY_SIZE;
        const y2 = y1 + this.LINE_HEIGHT;
        const contentBottom = Math.max(this.MARGIN + logoHeight, y2);
        const height = contentBottom + 10;
        const logoSvg = this.renderLogo(logo, this.MARGIN, this.MARGIN, logoMaxWidth, logoMaxHeight);
        const defs = [];
        const avatarCenterY = (y1 + y2) / 2 - this.BODY_SIZE * 0.35;
        const avatarSvg = this.renderPersonAvatar(options.person, avatarX, avatarCenterY, avatarSize, 'file-badge-avatar-clip', defs);
        const defsSvg = defs.length ? `<defs>\n  ${defs.join('\n  ')}\n  </defs>\n  ` : '';
        const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}">
  ${defsSvg}<rect x="0" y="0" width="${width}" height="${height}" fill="#ffffff" fill-opacity="0.75"/>
  ${logoSvg}
  ${avatarSvg}
  <text x="${textX}" y="${y1}" font-family="${this.FONT}" font-size="${this.BODY_SIZE}" font-weight="bold" fill="${textColor}">${this.escapeXml(line1)}</text>
  <text x="${textX}" y="${y2}" font-family="${this.FONT}" font-size="${this.BODY_SIZE}" fill="${subtextColor}">${this.escapeXml(line2)}</text>
  <line x1="${this.MARGIN}" y1="${height}" x2="${width - this.MARGIN}" y2="${height}" stroke="#cccccc" stroke-width="1"/>
</svg>`;
        return { svg, width, height };
    }
    static getAddressLines(address, city, state_code, zip) {
        return [address, `${city}, ${state_code} ${zip}`].filter(Boolean).map((line) => line.trim());
    }
    static getPrintedOnLabel() {
        const now = new Date();
        const date = now.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
        const time = now.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
        const timezone = now.toLocaleTimeString('en-US', { timeZoneName: 'short' }).split(' ').pop() || '';
        return `Printed On: ${date} ${time} ${timezone}`.trim();
    }
    static wrapText(text, maxWidth, fontSize) {
        if (!text)
            return [];
        const estimateWidth = (value) => value.length * fontSize * this.WRAP_CHAR_WIDTH_RATIO;
        const paragraphs = text.split(/\r?\n/);
        const lines = [];
        for (const paragraph of paragraphs) {
            const words = paragraph.split(/\s+/).filter(Boolean);
            if (words.length === 0) {
                lines.push('');
                continue;
            }
            let current = words[0];
            for (let i = 1; i < words.length; i++) {
                const candidate = `${current} ${words[i]}`;
                if (estimateWidth(candidate) <= maxWidth) {
                    current = candidate;
                }
                else {
                    lines.push(current);
                    current = words[i];
                }
            }
            lines.push(current);
        }
        return lines;
    }
    static decodeBase64Utf8(base64Data) {
        const binary = atob(base64Data);
        const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
        return new TextDecoder('utf-8').decode(bytes);
    }
    static parseSvgViewBox(svgMarkup, fallbackWidth, fallbackHeight) {
        const viewBox = svgMarkup.match(/viewBox\s*=\s*["']([^"']+)["']/i)?.[1]?.trim();
        if (viewBox)
            return viewBox;
        return `0 0 ${fallbackWidth || 1} ${fallbackHeight || 1}`;
    }
    static renderInlineSvgLogo(base64Data, x, y, width, height, sourceWidth, sourceHeight) {
        const svgContent = this.decodeBase64Utf8(base64Data);
        const innerMatch = svgContent.match(/<svg\b[^>]*>([\s\S]*)<\/svg>/i);
        if (!innerMatch)
            return '';
        const viewBox = this.parseSvgViewBox(svgContent, sourceWidth, sourceHeight);
        return `<svg x="${x}" y="${y}" width="${width}" height="${height}" viewBox="${viewBox}" preserveAspectRatio="xMidYMid meet">${innerMatch[1]}</svg>`;
    }
    static getPersonInitials(person) {
        if (!person)
            return '?';
        const first = person.first_name?.trim()?.charAt(0)?.toUpperCase() || '';
        const last = person.last_name?.trim()?.charAt(0)?.toUpperCase() || '';
        return `${first}${last}` || '?';
    }
    static escapeXml(text) {
        if (!text)
            return '';
        return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
    }
}
